import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tabs, Drawer } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getIssuancesApi, createIssuanceApi, getEmployeeUniformProfileApi, getUniformItemsApi, getWarehouseStockApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, issuanceStatusOptions } from '../../constants';
import Breadcrumbs from 'components/Breadcrumbs';
import EssFullWidthModal from 'components/modelpopup/baseModal';

const issuanceSchema = Yup.object().shape({
  company_user: Yup.object().nullable().required('Employee is required'),
  uniform_item: Yup.object().nullable().required('Uniform item is required'),
  size: Yup.object().nullable().when('uniform_item', {
    is: (item) => !!item && (item.sizes?.length ?? 0) > 0,
    then: (s) => s.required('Size is required'),
    otherwise: (s) => s.notRequired(),
  }),
  warehouse: Yup.object().nullable().required('Warehouse is required'),
  quantity: Yup.number().required('Quantity is required').min(1, 'Min 1'),
  issuance_date: Yup.string().required('Issuance date is required'),
  notes: Yup.string()
});

const IssuanceList = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const { userData, userDataLoading } = useSelector((state) => state.common);
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [employeeProfiles, setEmployeeProfiles] = useState([]);
  const [employeeUniformOptions, setEmployeeUniformOptions] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [overrideSize, setOverrideSize] = useState(false);
  const [autoSize, setAutoSize] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState(null);
  const [filterItem, setFilterItem] = useState(null);

  const employeeOptions = (userData || [])
    .slice()
    .sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' }))
    .map(u => ({
      label: `${u.first_name} ${u.last_name}${u.emp_no ? ` (${u.emp_no})` : ''}`,
      value: u.id, emp_no: u.emp_no || ''
    }));

  const { control, handleSubmit, formState: { errors }, reset, setValue, watch, setError } = useForm({
    resolver: yupResolver(issuanceSchema),
    defaultValues: {
      company_user: null, uniform_item: null, size: null,
      warehouse: null, quantity: 1, issuance_date: '', notes: ''
    }
  });

  const selectedEmployee = watch('company_user');
  const selectedUniformItem = watch('uniform_item');
  const selectedWarehouse = watch('warehouse');
  const selectedSize = watch('size');
  const watchedQuantity = watch('quantity');

  const sizeOptions = (selectedUniformItem?.sizes || [])
    .filter(s => s.status !== false)
    .map(s => ({ label: `${s.name} (${s.code})`, value: s.id }));
  const sizeRequired = sizeOptions.length > 0;

  const [availableQty, setAvailableQty] = useState(null);
  const [availableLoading, setAvailableLoading] = useState(false);

  useEffect(() => {
    if (!selectedWarehouse || !selectedUniformItem) {
      setAvailableQty(null);
      return;
    }
    if (sizeRequired && !selectedSize) {
      setAvailableQty(null);
      return;
    }
    setAvailableLoading(true);
    const params = { uniform_item: selectedUniformItem.value };
    if (selectedSize) params.size = selectedSize.value;
    else params.size_isnull = true;
    getWarehouseStockApi(selectedWarehouse.value, params).then(response => {
      const rows = response.data?.results || response.data?.data || [];
      const total = rows.reduce((sum, r) => sum + (r.quantity || 0), 0);
      setAvailableQty(total);
    }).catch(() => setAvailableQty(null))
      .finally(() => setAvailableLoading(false));
  }, [selectedWarehouse, selectedUniformItem, selectedSize, sizeRequired]);

  useEffect(() => {
    setUniformItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then(response => {
      setUniformItems(
        (response.data?.results || [])
          .map(i => ({ label: i.name, value: i.id, sizes: i.sizes || [] }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch(() => {})
      .finally(() => setUniformItemsLoading(false));
  }, []);

  // Re-attach `sizes` to employee uniform options once master uniformItems is loaded
  // (handles the race where profiles resolve before uniformItems).
  useEffect(() => {
    if (!uniformItems.length || !employeeProfiles.length) return;
    const next = employeeProfiles.map(p => {
      const fullItem = uniformItems.find(u => u.value === p.uniform_item);
      return {
        label: p.uniform_item_name,
        value: p.uniform_item,
        sizes: fullItem?.sizes || [],
      };
    }).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    setEmployeeUniformOptions(next);
    const current = watch('uniform_item');
    if (current) {
      const refreshed = next.find(o => o.value === current.value);
      if (refreshed && refreshed !== current) setValue('uniform_item', refreshed);
    }
  }, [uniformItems, employeeProfiles]);

  // Load employee's uniform profiles -> derive allowed uniform options.
  // Keep the chosen uniform_item if the new employee's profiles still include it
  // (i.e. same position / overlapping assignment). Otherwise clear it.
  useEffect(() => {
    if (!selectedEmployee) {
      setEmployeeProfiles([]);
      setEmployeeUniformOptions([]);
      setValue('uniform_item', null);
      setValue('size', null);
      setAutoSize(null);
      setOverrideSize(false);
      return;
    }
    setProfileLoading(true);
    getEmployeeUniformProfileApi(selectedEmployee.value).then(response => {
      const profiles = response.data?.data || [];
      setEmployeeProfiles(profiles);
      setEmployeeUniformOptions(
        profiles
          .map(p => {
            const fullItem = uniformItems.find(u => u.value === p.uniform_item);
            return {
              label: p.uniform_item_name,
              value: p.uniform_item,
              sizes: fullItem?.sizes || [],
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
      const currentItem = watch('uniform_item');
      const stillAllowed = currentItem
        && profiles.some(p => p.uniform_item === currentItem.value);
      if (!stillAllowed) {
        setValue('uniform_item', null);
        setValue('size', null);
        setAutoSize(null);
        setOverrideSize(false);
      }
    }).catch(() => {
      setEmployeeProfiles([]);
      setEmployeeUniformOptions([]);
      setValue('uniform_item', null);
      setValue('size', null);
      setAutoSize(null);
      setOverrideSize(false);
    }).finally(() => setProfileLoading(false));
  }, [selectedEmployee]);

  // When uniform item changes, auto-populate size from profile
  useEffect(() => {
    if (!selectedUniformItem) {
      setValue('size', null);
      setAutoSize(null);
      setOverrideSize(false);
      return;
    }
    const match = employeeProfiles.find(p => p.uniform_item === selectedUniformItem.value);
    if (match?.size_detail) {
      const sizeOpt = {
        label: `${match.size_detail.name} (${match.size_detail.code})`,
        value: match.size_detail.id
      };
      setValue('size', sizeOpt);
      setAutoSize(sizeOpt);
      setOverrideSize(false);
    } else {
      setValue('size', null);
      setAutoSize(null);
      setOverrideSize(true); // no profile size -> must pick manually
    }
  }, [selectedUniformItem, employeeProfiles]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = { page: pageSettings.page, page_size: pageSettings.pageSize };
    if (statusFilter) params.status = statusFilter;
    if (filterEmployee) params.company_user = filterEmployee.value;
    if (filterItem) params.uniform_item = filterItem.value;
    getIssuancesApi(params).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize, statusFilter, filterEmployee, filterItem]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const onCreateSubmit = (formData) => {
    setCreateLoading(true);
    createIssuanceApi({
      company_user: formData.company_user.value,
      uniform_item: formData.uniform_item.value,
      size: formData.size?.value || null,
      warehouse: formData.warehouse.value,
      quantity: formData.quantity,
      issuance_date: formData.issuance_date,
      notes: formData.notes || ''
    }).then(() => {
      toast.success('Issuance created', { position: "top-right", autoClose: 3000, transition: Bounce });
      setCreateLoading(false);
      setShowCreateModal(false);
      reset();
      fetchData();
    }).catch(e => {
      setCreateLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Failed to create issuance';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const columns = [
    {
      title: 'Employee', dataIndex: 'company_user_name',
      render: (text, record) => (
        <div>
          <a onClick={() => navigate(`/uniform-management/issuance/${record.id}`)}
            style={{ cursor: 'pointer', color: '#D3001A' }}>{text}</a>
          <br /><small className="text-muted">{record.company_user_emp_no}</small>
        </div>
      )
    },
    { title: 'Uniform Item', dataIndex: 'uniform_item_name' },
    {
      title: 'Size', dataIndex: 'size_detail',
      render: (detail) => detail ? `${detail.name} (${detail.code})` : '-'
    },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Issued By', dataIndex: 'issued_by_name' },
    {
      title: 'Next Replacement', dataIndex: 'next_replacement_date',
      render: (d) => d ? formatDateHandler(d) : '-'
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status}</span>
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="View" className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/uniform-management/issuance/${record.id}`)} />
      )
    }
  ];

  const tabItems = [
    { key: '', label: 'All' },
    ...issuanceStatusOptions.map(o => ({ key: o.value, label: o.label }))
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Uniform Issuance" />
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2">
<Button label="Filters" icon="fa fa-filter" className="btn btn-outline-primary btn-sm"
              onClick={() => setShowFilter(true)} />
          </div>
          <div className="d-flex gap-2">
            <Button label="Bulk Issuance" icon="fa fa-users" className="btn btn-outline-primary btn-sm"
              onClick={() => navigate('/uniform-management/bulk-issuance')} />
            <Button label="New Issuance" icon="fa fa-plus" className="btn btn-primary btn-sm"
              onClick={() => { reset(); setShowCreateModal(true); }} />
          </div>
        </div>

        <Tabs activeKey={statusFilter}
          onChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          items={tabItems} />

        <Table columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{
            total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
            pageSizeOptions, itemRender, showSizeChanger: true
          }}
          rowClassName="clickable-row"
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest('a, button')) return;
              navigate(`/uniform-management/issuance/${record.id}`);
            },
          })}
          onChange={handleChange} scroll={{ x: "max-content" }} />

        {/* Filter Drawer */}
        <Drawer title="Filters" placement="right" open={showFilter} onClose={() => setShowFilter(false)}>
          <div className="mb-3">
            <label className="col-form-label">Employee</label>
            <FilterSelect isClearable onChange={setFilterEmployee} optionLabel="label" optionValue="value"
              options={employeeOptions} value={filterEmployee} placeholder="Filter by Employee" isLoading={userDataLoading} />
          </div>
          <div className="mb-3">
            <label className="col-form-label">Uniform Item</label>
            <FilterSelect isClearable onChange={setFilterItem} optionLabel="label" optionValue="value"
              options={uniformItems} value={filterItem} placeholder="Filter by Item" isLoading={uniformItemsLoading} />
          </div>
          <Button label="Apply" className="btn btn-primary w-100"
            onClick={() => { setPageSettings(prev => ({ ...prev, page: 1 })); setShowFilter(false); }} />
        </Drawer>

        {/* Create Issuance Modal */}
        <EssFullWidthModal mask={false} title="New Issuance" open={showCreateModal}
          onCancel={() => setShowCreateModal(false)} className="full-width-modal">
          <div className="modal-content">
            <div className="modal-body">
              <form onSubmit={handleSubmit(onCreateSubmit)}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Employee <span className="text-danger">*</span></label>
                      <Controller name="company_user" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                            options={employeeOptions} value={value} placeholder="Select Employee" isLoading={userDataLoading} />
                        )} />
                      {errors.company_user && <small className="text-danger">{errors.company_user.message}</small>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Uniform Item <span className="text-danger">*</span></label>
                      <Controller name="uniform_item" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect
                            isClearable
                            onChange={onChange}
                            optionLabel="label"
                            optionValue="value"
                            options={employeeUniformOptions}
                            value={value}
                            placeholder={selectedEmployee ? "Select Item" : "Select Employee First"}
                            isLoading={profileLoading}
                            isDisabled={!selectedEmployee}
                          />
                        )} />
                      {errors.uniform_item && <small className="text-danger">{errors.uniform_item.message}</small>}
                      {selectedEmployee && !profileLoading && employeeUniformOptions.length === 0 && (
                        <small className="text-warning">No uniforms assigned to this employee's position.</small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label d-flex align-items-center justify-content-between">
                        <span>
                          Size {sizeRequired && <span className="text-danger">*</span>}
                          {selectedUniformItem && !sizeRequired && (
                            <small className="text-muted ms-2">(no sizes assigned to this item)</small>
                          )}
                        </span>
                        {autoSize && sizeRequired && (
                          <button type="button" className="btn btn-link btn-sm p-0"
                            onClick={() => setOverrideSize(v => !v)}>
                            {overrideSize ? "Use profile size" : "Change size"}
                          </button>
                        )}
                      </label>
                      {!sizeRequired ? (
                        <input className="form-control" value="—" readOnly disabled />
                      ) : !overrideSize && autoSize ? (
                        <input className="form-control" value={autoSize.label} readOnly disabled />
                      ) : (
                        <Controller name="size" control={control}
                          render={({ field: { value, onChange } }) => (
                            <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                              options={sizeOptions} value={value} placeholder="Select Size" isLoading={false} />
                          )} />
                      )}
                      {errors.size && <small className="text-danger">{errors.size.message}</small>}
                      {sizeRequired && !autoSize && selectedUniformItem && (
                        <small className="text-muted">Employee has no size on profile — pick manually.</small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Warehouse <span className="text-danger">*</span></label>
                      <Controller name="warehouse" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                            options={warehouseOptions} value={value} placeholder="Select Warehouse" isLoading={warehousesLoading} />
                        )} />
                      {errors.warehouse && <small className="text-danger">{errors.warehouse.message}</small>}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label d-flex justify-content-between align-items-center">
                        <span>Quantity <span className="text-danger">*</span></span>
                        {availableLoading && <small className="text-muted">Checking stock…</small>}
                        {!availableLoading && availableQty !== null && (
                          <small className={availableQty > 0 ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                            Available: {availableQty}
                          </small>
                        )}
                      </label>
                      <Controller name="quantity" control={control}
                        render={({ field }) => <input className="form-control" {...field} type="number" min="1"
                          max={availableQty ?? undefined} />} />
                      {errors.quantity && <small className="text-danger">{errors.quantity.message}</small>}
                      {availableQty !== null && Number(watchedQuantity) > availableQty && (
                        <small className="text-danger d-block">Exceeds available stock</small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Issuance Date <span className="text-danger">*</span></label>
                      <Controller name="issuance_date" control={control}
                        render={({ field }) => <input className="form-control" {...field} type="date" />} />
                      {errors.issuance_date && <small className="text-danger">{errors.issuance_date.message}</small>}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Notes</label>
                      <Controller name="notes" control={control}
                        render={({ field }) => <input className="form-control" {...field} placeholder="Notes" />} />
                    </div>
                  </div>
                </div>
                <div className="submit-section">
                  <Button
                    loading={createLoading}
                    disabled={
                      createLoading
                      || availableLoading
                      || (availableQty !== null && Number(watchedQuantity) > availableQty)
                    }
                    className="btn btn-primary submit-btn"
                    type="submit" label="Issue Uniform" />
                </div>
              </form>
            </div>
          </div>
        </EssFullWidthModal>
      </div>
    </div>
  );
};

export default IssuanceList;
